import { useState, useEffect, useRef } from 'react';
import { Volume2, X } from 'lucide-react';
import { useTTS } from '../../hooks/useTTS';
import { API } from '../../App';
import axios from 'axios';

const BuildingChallenge = ({ building, onComplete, onClose, voiceEnabled = true, playerVoice = 'alice' }) => {
  const [phrase, setPhrase] = useState('');
  const [category, setCategory] = useState('');
  const [typedText, setTypedText] = useState('');
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying } = useTTS();

  useEffect(() => {
    fetchPhrase();
    return () => stopTTS();
  }, [building]);

  const fetchPhrase = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/island/building-phrase?building_type=${encodeURIComponent(building.type)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhrase(res.data.phrase);
      setCategory(res.data.category);
      setTypedText('');
      setCompleted(false);
      setTimeout(() => {
        inputRef.current?.focus();
        if (voiceEnabled) speakAsPlayer(res.data.phrase, playerVoice);
      }, 300);
    } catch (e) {
      setPhrase('type this phrase');
    } finally {
      setLoading(false);
    }
  };

  const handleTyping = (e) => {
    const value = e.target.value;
    if (value.length <= phrase.length) {
      setTypedText(value);
    }
    if (value.toLowerCase() === phrase.toLowerCase()) {
      stopTTS();
      setCompleted(true);
      // Save to typing sessions as building challenge
      const token = localStorage.getItem('typetown_token');
      axios.post(`${API}/typing/session`, {
        phrase: `building:${building.type}`,
        typed_text: value,
        keystrokes: value.length,
        correct_keystrokes: value.length,
        duration_seconds: 5,
        is_challenge: false,
        is_duel: false,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };

  const readAloud = () => {
    if (phrase) speakAsPlayer(phrase, playerVoice);
  };

  const accuracy = typedText.length > 0
    ? Math.round(typedText.split('').filter((c, i) => c === phrase[i]).length / typedText.length * 100)
    : 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[500] flex items-center justify-center p-4" data-testid="building-challenge-modal">
      <div className="bg-white border-3 border-mint-green rounded-2xl p-6 max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-text-medium text-xs font-semibold uppercase tracking-wide">{category}</p>
            <h3 className="font-display text-xl text-text-dark">{building.name || building.type}</h3>
          </div>
          <div className="flex items-center gap-2">
            {voiceEnabled && (
              <button onClick={readAloud} disabled={isPlaying} className={`w-8 h-8 rounded-full flex items-center justify-center ${isPlaying ? 'bg-mint-green/20 text-mint-green animate-pulse' : 'bg-lavender/20 text-lavender hover:bg-lavender/30'}`} data-testid="building-listen-btn">
                <Volume2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => { stopTTS(); onClose(); }} className="w-8 h-8 bg-coral-orange/20 hover:bg-coral-orange/40 rounded-full flex items-center justify-center text-coral-orange">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-8 text-center text-text-medium">Loading...</div>
        ) : completed ? (
          <div className="text-center py-6" data-testid="building-challenge-complete">
            <div className="w-14 h-14 bg-mint-green/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">+2</span>
            </div>
            <p className="font-display text-2xl text-mint-green mb-2">Nice one!</p>
            <p className="text-text-medium text-sm mb-4">+2 coins earned</p>
            <div className="flex gap-3">
              <button onClick={fetchPhrase} className="flex-1 py-3 bg-gradient-to-r from-mint-green to-sky-blue text-white font-display rounded-xl" data-testid="building-another-btn">
                Another!
              </button>
              <button onClick={() => { stopTTS(); onComplete(); }} className="px-6 py-3 bg-soft-gray/30 text-text-medium font-display rounded-xl" data-testid="building-done-btn">
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Phrase display */}
            <div className="bg-gradient-to-r from-cream to-warm-white rounded-xl p-5 mb-4 border-2 border-mint-green/20">
              <p className="text-center font-mono text-xl leading-relaxed">
                {phrase.split('').map((char, i) => {
                  const isTyped = i < typedText.length;
                  const isCorrect = isTyped && typedText[i] === char;
                  const isCurrent = i === typedText.length;
                  return (
                    <span key={i} className={
                      isTyped ? (isCorrect ? 'text-mint-green' : 'text-coral-orange bg-coral-orange/20 rounded')
                      : isCurrent ? 'text-sunny-yellow bg-sunny-yellow/20 rounded animate-pulse font-bold'
                      : 'text-text-medium/50'
                    }>
                      {char === ' ' ? '\u00A0' : char}
                    </span>
                  );
                })}
              </p>
            </div>

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={typedText}
              onChange={handleTyping}
              onKeyDown={(e) => { if (e.key === 'Escape') { stopTTS(); onClose(); } }}
              className="w-full px-4 py-3 bg-white border-2 border-mint-green/40 rounded-xl text-text-dark text-center font-mono text-lg focus:outline-none focus:border-mint-green focus:ring-2 focus:ring-mint-green/20"
              placeholder="Type here..."
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              data-testid="building-challenge-input"
            />

            {/* Progress */}
            <div className="mt-3 flex justify-between text-xs text-text-medium">
              <span>{typedText.length}/{phrase.length}</span>
              <span className={accuracy >= 80 ? 'text-mint-green' : 'text-coral-orange'}>{accuracy}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuildingChallenge;
